import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';
import { websiteImages } from '../config/images';
import { Menu, X, Star, Facebook, Instagram, Phone } from 'lucide-react';

const Header = () => {
  const { language, setLanguage } = useLanguage();
  const t = translations[language];
  const navigate = useNavigate();
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
    setIsMobileMenuOpen(false);
  };

  // Works from any page: if not on home, go home first then scroll to the section
  const goToSection = (sectionId) => {
    setIsMobileMenuOpen(false);
    if (location.pathname !== '/') {
      navigate('/', { state: { scrollTo: sectionId } });
    } else {
      scrollToSection(sectionId);
    }
  };

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-black/95 backdrop-blur-md shadow-lg' : 'bg-black/80'}`}>
      {/* Top Bar */}
      <div className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-2 text-sm">
            {/* Left Side - Delivery Platforms */}
            <div className="flex items-center gap-3 flex-wrap">
              <a href="https://order.site/cobarci/tr/deu/berlin/restaurant/corbaci-sf?no_universal_links=true" target="_blank" rel="noopener noreferrer" className="text-white/80 hover:text-[#d4af37] transition-colors duration-200">
                Wolt
              </a>
              <span className="text-white/30">|</span>
              <a href="https://www.ubereats.com/de/store/corbaci-authentische-turkische-kuche/Z4L9pQSxVnyHd6TEiOAjOQ" target="_blank" rel="noopener noreferrer" className="text-white/80 hover:text-[#d4af37] transition-colors duration-200">
                Uber Eats
              </a>
              <span className="text-white/30">|</span>
              <a href="https://www.lieferando.de/speisekarte/corbaci-10627" target="_blank" rel="noopener noreferrer" className="text-white/80 hover:text-[#d4af37] transition-colors duration-200">
                Lieferando
              </a>
              <span className="text-white/30">|</span>
              <a href="https://wa.me/493089542916" target="_blank" rel="noopener noreferrer" className="text-white/80 hover:text-[#d4af37] transition-colors duration-200">
                WhatsApp
              </a>
            </div>

            {/* Right Side - Social & Review */}
            <div className="flex items-center gap-4">
              <a href="https://www.instagram.com/corbaciberlin/" target="_blank" rel="noopener noreferrer" className="text-white/80 hover:text-[#d4af37] transition-colors duration-200">
                <Instagram className="w-4 h-4" />
              </a>
              <a href="https://www.facebook.com/corbaciberlin/" target="_blank" rel="noopener noreferrer" className="text-white/80 hover:text-[#d4af37] transition-colors duration-200">
                <Facebook className="w-4 h-4" />
              </a>
              <a href="https://www.tiktok.com/@corbaciberlin?_r=1&_t=ZG-997dD8C2hnc" target="_blank" rel="noopener noreferrer" aria-label="TikTok" className="text-white/80 hover:text-[#d4af37] transition-colors duration-200">
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path d="M16.5 3c.3 2.1 1.6 3.9 3.5 4.5v3c-1.4 0-2.7-.4-3.9-1.1v6.1c0 3.2-2.6 5.8-5.8 5.8S4.5 18.7 4.5 15.5 7.1 9.7 10.3 9.7c.3 0 .6 0 .9.1v3.1c-.3-.1-.6-.2-.9-.2-1.5 0-2.7 1.2-2.7 2.7s1.2 2.7 2.7 2.7 2.7-1.2 2.7-2.7V3h3z"/></svg>
              </a>
              <a href="https://www.google.com/maps/place//data=!4m3!3m2!1s0x47a851e29a4030c5:0xb4caa9cba7e8b0f5!12e1?source=g.page.m.dd._&laa=lu-desktop-reviews-dialog-review-solicitation" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 hover:opacity-80 transition-opacity duration-200" title={t.give_review}>
                <svg className="w-5 h-5" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z" fill="#FFC107"/>
                  <path d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z" fill="#FF3D00"/>
                  <path d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0124 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z" fill="#4CAF50"/>
                  <path d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 01-4.087 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z" fill="#1976D2"/>
                </svg>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between py-4">
          {/* Logo */}
          <div className="flex items-center cursor-pointer" onClick={() => goToSection('home')}>
            <img data-testid="header-logo" src={websiteImages.logo.url} alt={websiteImages.logo.alt} className="h-14 sm:h-16 w-auto" />
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            <button data-testid="nav-home" onClick={() => goToSection('home')} className="text-white hover:text-[#d4af37] transition-colors duration-200 font-medium">{t.nav_home}</button>
            <button data-testid="nav-about" onClick={() => goToSection('about')} className="text-white hover:text-[#d4af37] transition-colors duration-200 font-medium">{t.nav_about}</button>
            <button data-testid="nav-gallery" onClick={() => goToSection('gallery')} className="text-white hover:text-[#d4af37] transition-colors duration-200 font-medium">{t.nav_gallery}</button>
            <a data-testid="nav-menu" href="/speisekarte" className="text-white hover:text-[#d4af37] transition-colors duration-200 font-medium">{t.nav_menu}</a>
            <a data-testid="nav-order" href="/bestellung" className="text-white hover:text-[#d4af37] transition-colors duration-200 font-medium">{t.nav_order}</a>
            <button data-testid="nav-contact" onClick={() => goToSection('contact')} className="text-white hover:text-[#d4af37] transition-colors duration-200 font-medium">{t.nav_contact}</button>
          </nav>

          {/* Phone & Order Button & Language */}
          <div className="flex items-center gap-4">
            <div className="hidden md:flex items-center gap-3">
              <a href="tel:+493089542916" className="flex items-center gap-2 text-white/90 hover:text-[#d4af37] transition-colors duration-200 font-medium">
                <Phone className="w-4 h-4 text-[#d4af37]" />
                +49 30 8954 2916
              </a>
              <a href="/bestellung" className="bg-[#d4af37] text-black px-6 py-2 rounded-md font-semibold hover:bg-[#f0c24d] transition-all duration-200 transform hover:scale-105">
                {t.order_now}
              </a>
            </div>

            {/* Language Switcher */}
            <div className="flex items-center gap-2 bg-white/10 rounded-md px-2 py-1">
              <button onClick={() => setLanguage('de')} className={`px-2 py-1 text-xs font-medium rounded transition-colors duration-200 ${language === 'de' ? 'bg-[#d4af37] text-black' : 'text-white/70 hover:text-white'}`}>DE</button>
              <button onClick={() => setLanguage('en')} className={`px-2 py-1 text-xs font-medium rounded transition-colors duration-200 ${language === 'en' ? 'bg-[#d4af37] text-black' : 'text-white/70 hover:text-white'}`}>EN</button>
              <button onClick={() => setLanguage('tr')} className={`px-2 py-1 text-xs font-medium rounded transition-colors duration-200 ${language === 'tr' ? 'bg-[#d4af37] text-black' : 'text-white/70 hover:text-white'}`}>TR</button>
            </div>

            {/* Mobile Menu Button */}
            <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="lg:hidden text-white p-2">
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-black/98 border-t border-white/10">
          <div className="max-w-7xl mx-auto px-4 py-4 space-y-3">
            <button onClick={() => goToSection('home')} className="block w-full text-left text-white hover:text-[#d4af37] transition-colors duration-200 font-medium py-2">{t.nav_home}</button>
            <button onClick={() => goToSection('about')} className="block w-full text-left text-white hover:text-[#d4af37] transition-colors duration-200 font-medium py-2">{t.nav_about}</button>
            <button onClick={() => goToSection('gallery')} className="block w-full text-left text-white hover:text-[#d4af37] transition-colors duration-200 font-medium py-2">{t.nav_gallery}</button>
            <a href="/speisekarte" className="block w-full text-left text-white hover:text-[#d4af37] transition-colors duration-200 font-medium py-2">{t.nav_menu}</a>
            <a href="/bestellung" className="block w-full text-left text-white hover:text-[#d4af37] transition-colors duration-200 font-medium py-2">{t.nav_order}</a>
            <button onClick={() => goToSection('contact')} className="block w-full text-left text-white hover:text-[#d4af37] transition-colors duration-200 font-medium py-2">{t.nav_contact}</button>
            <div className="pt-3 border-t border-white/10">
              <a href="tel:+493089542916" className="block text-white/90 hover:text-[#d4af37] transition-colors duration-200 font-medium py-2">
                📞 +49 30 8954 2916
              </a>
              <a href="/bestellung" className="block bg-[#d4af37] text-black px-6 py-3 rounded-md font-semibold hover:bg-[#f0c24d] transition-colors duration-200 mt-2 text-center">
                {t.order_now}
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
